import { logger } from './logger';

// JSON 직렬화를 위한 안전한 데이터 정리 함수
export const sanitizeForJSON = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (typeof obj === 'string') {
    // eslint-disable-next-line no-control-regex
    return obj.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
  }
  
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForJSON(item)).filter(item => item !== null);
  }
  
  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // eslint-disable-next-line no-control-regex
      const safeKey = key.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
      const sanitizedValue = sanitizeForJSON(value);
      
      // null 값은 제외하거나 기본값으로 대체
      if (sanitizedValue !== null) {
        sanitized[safeKey] = sanitizedValue;
      } else {
        // null 값을 적절한 기본값으로 대체
        if (typeof value === 'number') {
          sanitized[safeKey] = 0;
        } else if (typeof value === 'string') {
          sanitized[safeKey] = '';
        } else if (Array.isArray(value)) {
          sanitized[safeKey] = [];
        } else if (typeof value === 'object') {
          sanitized[safeKey] = {};
        } else {
          sanitized[safeKey] = null;
        }
      }
    }
    return sanitized;
  }
  
  // 기타 타입은 문자열로 변환
  return String(obj);
};

// 안전한 JSON 직렬화 함수
export const safeStringify = (obj: any): string => {
  try {
    const sanitized = sanitizeForJSON(obj);
    return JSON.stringify(sanitized);
  } catch (error) {
    logger.error('JSON 직렬화 실패:', error);
    // 실패 시 기본 객체로 대체
    return JSON.stringify({ 
      type: 'error', 
      message: 'JSON serialization failed',
      timestamp: new Date().toISOString()
    });
  }
};

// pinpoint 전송용 데이터 정리 함수
export const preparePinpointData = (data: any): any => {
  const cleaned = sanitizeForJSON(data);
  
  // 추가적인 pinpoint 특화 정리
  if (cleaned && typeof cleaned === 'object') {
    // 모든 문자열 값을 안전하게 처리
    Object.keys(cleaned).forEach(key => {
      if (typeof cleaned[key] === 'string') {
        // 문자열 길이 제한 (너무 긴 문자열 방지)
        if (cleaned[key].length > 1000) {
          cleaned[key] = cleaned[key].substring(0, 1000) + '...';
        }
        // 특수 문자 제거
        cleaned[key] = cleaned[key].replace(/[^\x20-\x7E]/g, '');
      }
    });
    
    // orderItems 배열이 있는 경우 특별 처리
    if (cleaned.data && cleaned.data.orderItems && Array.isArray(cleaned.data.orderItems)) {
      cleaned.data.orderItems = cleaned.data.orderItems.map((item: any) => {
        if (item && typeof item === 'object') {
          // null 값들을 안전한 기본값으로 대체
          const safeItem = { ...item };
          
          // categoryLimits 객체 처리
          if (safeItem.categoryLimits) {
            safeItem.categoryLimits = {
              maxAllowed: safeItem.categoryLimits.maxAllowed ?? null,
              orderIntervalMinutes: safeItem.categoryLimits.orderIntervalMinutes ?? null
            };
          }
          
          // 기타 null 값들 처리
          safeItem.maxAllowed = safeItem.maxAllowed ?? null;
          safeItem.minRequired = safeItem.minRequired ?? 1;
          safeItem.maxAllowedPerTable = safeItem.maxAllowedPerTable ?? null;
          safeItem.itemPrice = safeItem.itemPrice ?? 0;
          safeItem.itemWithModifierPrice = safeItem.itemWithModifierPrice ?? 0;
          safeItem.modifierPrice = safeItem.modifierPrice ?? 0;
          safeItem.subtotalAmount = safeItem.subtotalAmount ?? 0;
          safeItem.quantity = safeItem.quantity ?? 1;
          
          return safeItem;
        }
        return item;
      });
    }
  }
  
  return cleaned;
};

// 배열 내 null 값들을 안전하게 처리하는 함수
export const sanitizeArrayForJSON = (arr: any[]): any[] => {
  return arr.map(item => {
    if (item === null || item === undefined) {
      return null;
    }
    
    if (typeof item === 'object') {
      const sanitized: any = {};
      for (const [key, value] of Object.entries(item)) {
        if (value === null || value === undefined) {
          // null 값을 적절한 기본값으로 대체
          if (key.includes('Price') || key.includes('Amount') || key.includes('maxAllowed') || key.includes('minRequired')) {
            sanitized[key] = 0;
          } else if (key.includes('orderIntervalMinutes')) {
            sanitized[key] = null;
          } else if (Array.isArray(value)) {
            sanitized[key] = [];
          } else if (typeof value === 'object') {
            sanitized[key] = {};
          } else {
            sanitized[key] = null;
          }
        } else {
          sanitized[key] = sanitizeForJSON(value);
        }
      }
      return sanitized;
    }
    
    return sanitizeForJSON(item);
  }).filter(item => item !== null);
}; 